import { getCalendarFeed, MoreIcon, Popover, renewCalendarFeed } from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/** A branch of the trip, as the menu offers it: its name in its colour. */
export interface MenuKin {
  id: string;
  name: string;
  place: number;
  /** Shown in the calendar now. */
  seen: boolean;
}

interface Props {
  trip: string;
  /** The trips it parts from, shown when ticked here; its branches, shown unless unticked. */
  kin: readonly MenuKin[];
  /** A trip ticked or unticked: shown, or no longer. */
  onFlip: (id: string) => void;
}

/**
 * The calendar's menu at the page's top right: taken elsewhere — a file for one's own calendar,
 * a link a calendar app keeps up to date by itself, or every month as a PDF — and which branches of the trip it shows
 * too.
 */
export function CalendarMenu({ trip, kin, onFlip }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ics = `/_/api/trips/${trip}/calendar.ics`;
  return (
    <Popover
      open={open}
      align="end"
      onOpenChange={(next) => {
        setOpen(next);
        setCopied(false);
      }}
      label={t('calendar.menu')}
      trigger={(props) => (
        <button
          type="button"
          className="icon-button calendar-menu"
          aria-label={t('calendar.menu')}
          title={t('calendar.menu')}
          {...props}
        >
          <MoreIcon />
        </button>
      )}
    >
      <div className="data-menu calendar-menu-panel">
        <p className="calendar-menu-head" id="calendar-export">
          {t('calendar.export')}
        </p>
        <div className="data-menu" role="menu" aria-labelledby="calendar-export">
          <a
            className="data-menu-item"
            role="menuitem"
            title={t('calendar.icsHint')}
            href={ics}
            download
            onClick={() => {
              setOpen(false);
            }}
          >
            {t('calendar.ics')}
          </a>
          <button
            type="button"
            role="menuitem"
            className="data-menu-item"
            title={t('calendar.subscribeHint')}
            onClick={() => {
              // The trip's own link, read without a session; made the first time.
              getCalendarFeed(trip)
                .then((link) => link ?? renewCalendarFeed(trip))
                .then((link) => navigator.clipboard.writeText(link))
                .then(
                  () => {
                    setCopied(true);
                  },
                  () => undefined,
                );
            }}
          >
            {t(copied ? 'calendar.copied' : 'calendar.subscribe')}
          </button>
          <button
            type="button"
            role="menuitem"
            className="data-menu-item"
            title={t('calendar.pdfHint')}
            onClick={() => {
              setOpen(false);
              window.print();
            }}
          >
            {t('calendar.pdf')}
          </button>
        </div>
        {kin.length > 0 && (
          <fieldset className="calendar-menu-kin">
            <legend className="calendar-menu-head">{t('calendar.alsoShown')}</legend>
            {kin.map((one) => (
              <label key={one.id} className={`check kin-${String(one.place % 6)}`}>
                <input
                  type="checkbox"
                  checked={one.seen}
                  onChange={() => {
                    onFlip(one.id);
                  }}
                />
                {one.name}
              </label>
            ))}
          </fieldset>
        )}
      </div>
    </Popover>
  );
}
